import type { InstrumentSetupRow } from "../types/instrumentSetup";
import type { ResolveInstrumentResponse } from "../types/instruments";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import { setMarketDataPreferencesBatch } from "../services/instrumentsApi";
import {
  buildInstrumentSetupBatchItem,
  type InstrumentRowEditorSelection,
} from "./instrumentSetupSave";
import {
  humanizeMarketDataBrokerSlug,
  marketDataBrokerKey,
} from "./marketDataSourceChoice";
import type { MarketDataSourceConnectAction } from "./marketDataConnectGate";

/** Brokers that support inline reconnect from instrument market data (no full connect wizard). */
export function supportsInlineMarketDataBrokerReconnect(
  broker: string | undefined | null
): boolean {
  const normalized = (broker ?? "").trim().toLowerCase();
  return (
    normalized === "liquidbrokers" ||
    normalized === "liquid" ||
    normalized === "ctrader"
  );
}

export function isInlineLiquidMarketDataReconnect(broker: string | undefined | null): boolean {
  const normalized = (broker ?? "").trim().toLowerCase();
  return normalized === "liquidbrokers" || normalized === "liquid";
}

export function isInlineCtraderMarketDataReconnect(broker: string | undefined | null): boolean {
  return (broker ?? "").trim().toLowerCase() === "ctrader";
}

function brokerFeedLaneUnavailableReason(
  source?: MarketDataSourceOption | null
): string {
  return (source?.unavailable_reason ?? "").trim().toLowerCase();
}

/**
 * BE marks a BYOK OHLC lane disconnected when `market_data_sources[].available === false`
 * and `unavailable_reason === "broker_not_connected"`.
 */
export function isBrokerFeedOhlcLaneDisconnected(
  source?: MarketDataSourceOption | null
): boolean {
  if (!source || source.available !== false) return false;
  return brokerFeedLaneUnavailableReason(source) === "broker_not_connected";
}

/**
 * Account-level repair: connected broker login exists but OHLC fetch consent was never
 * granted (OAuth `md_fetch_consent` / Liquid credential flag). BE sets the lane
 * unavailable with `unavailable_reason === "market_data_consent_required"`.
 */
export function isBrokerFeedOhlcAccountConsentRepair(
  source?: MarketDataSourceOption | null
): boolean {
  if (!source || source.available !== false) return false;
  return brokerFeedLaneUnavailableReason(source) === "market_data_consent_required";
}

/**
 * OAuth / credential reconnect dialog — only when BE marks the OHLC lane unavailable.
 * When `available === true`, the broker is already connected: use inline Layer 2 consent
 * + batch preference save (checkbox + Confirm).
 */
export function needsBrokerFeedReconnectConfirm(input: {
  row: Pick<
    InstrumentSetupRow,
    | "requires_market_data_consent"
    | "saved_market_data_broker"
    | "market_data_broker"
    | "saved_ibkr_conid"
    | "saved_ibkr_exchange"
  >;
  source?: MarketDataSourceOption | null;
  connectAction?: MarketDataSourceConnectAction | null;
}): boolean {
  const broker = marketDataBrokerKey(input.source ?? {});
  if (!supportsInlineMarketDataBrokerReconnect(broker)) return false;

  if (!input.source || input.source.available !== false) {
    return false;
  }

  return (
    isBrokerFeedOhlcLaneDisconnected(input.source) ||
    isBrokerFeedOhlcAccountConsentRepair(input.source)
  );
}

export function resolveMarketDataBrokerReconnectConsentLabel(input: {
  symbol: string;
  source?: MarketDataSourceOption | null;
  broker?: string;
}): string {
  const symbol = input.symbol.trim();
  const brokerSlug = marketDataBrokerKey(input.source ?? { broker: input.broker });
  const provider =
    input.source?.broker_display?.trim() ||
    input.source?.market_data_broker_display?.trim() ||
    humanizeMarketDataBrokerSlug(brokerSlug) ||
    "broker";
  return (
    `I consent to Tessera fetching OHLC from my connected ${provider} account for ${symbol} ` +
    "on this trade account, and authorize Tessera to use this broker connection for charting and analysis."
  );
}

export function buildInlineLiquidBrokerCredentialPayload(input: {
  username: string;
  password: string;
  domain?: string;
  mdFetchConsent: boolean;
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    username: input.username.trim(),
    password: input.password,
    domain: (input.domain ?? "default").trim() || "default",
  };
  if (input.mdFetchConsent) {
    payload.md_fetch_consent = true;
  }
  return payload;
}

export async function saveInstrumentMarketDataPreferenceAfterReconnect(input: {
  brokerAccountCode: string;
  symbol: string;
  row: InstrumentSetupRow;
  selection: InstrumentRowEditorSelection;
  resolveContext?: Pick<
    ResolveInstrumentResponse,
    "md_listing_key" | "instrument_id" | "asset_class"
  >;
}): Promise<void> {
  const batchItem = buildInstrumentSetupBatchItem(
    input.brokerAccountCode,
    input.symbol.trim(),
    input.selection,
    input.resolveContext,
    input.row,
    true
  );
  const batchResponse = await setMarketDataPreferencesBatch({
    broker_account_code: input.brokerAccountCode,
    preferences: [batchItem],
  });
  const saved = batchResponse.saved?.find((item) => item.symbol === input.symbol.trim());
  if (!batchResponse.ok || batchResponse.saved_count < 1 || !saved?.ok) {
    throw new Error(
      saved && !saved.ok
        ? `Market data preference for ${input.symbol} was not saved.`
        : "Market data preference save failed."
    );
  }
}
